import { initContract } from "@ts-rest/core";
import { z } from "zod";
import {
    EntityChangeWarningsSchema,
    IntegrationSyncStatusSchema,
} from "@common/schemas/domains/financialAccount/financial-account.schema";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import { FinancialAccountDto } from "../dto/financialAccount/financial-account.dto";

const c = initContract();

export const financialAccountContract = c.router(
    {
        deleteAccount: {
            method: "DELETE",
            path: "/:financialAccountId",
            pathParams: z.object({
                financialAccountId: z.coerce.number(),
            }),
            query: z.object({
                forceDeletion: z
                    .enum(["true", "false"])
                    .transform((v) => v === "true")
                    .optional(),
            }),
            responses: {
                204: c.noBody(),
                404: ErrorMessageSchema,
            },
        },
        updateAccount: {
            method: "PATCH",
            path: "/:financialAccountId",
            pathParams: z.object({
                financialAccountId: z.coerce.number(),
            }),
            body: z.object({
                name: z.string().optional(),
                bookkeepingStartDate: z.string().date().nullable().optional(),
                bookkeepingEndDate: z.string().date().nullable().optional(),
                isContinuousReconciliationEnabled: z.boolean().optional(),
            }),
            responses: {
                200: c.type<FinancialAccountDto>(),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        extractMergedAccount: {
            method: "POST",
            path: "/extract-merged-account",
            body: z.object({
                financialAccountId: z.number(),
                entityId: z.number(),
                integrationAccountToExtractId: z.string().uuid(),
            }),
            responses: {
                201: c.type<{
                    extractedFinancialAccount: FinancialAccountDto;
                }>(),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getIntegrationSyncStatus: {
            method: "GET",
            path: "/:financialAccountId/integration-sync-status",
            pathParams: z.object({
                financialAccountId: z.coerce.number(),
            }),
            responses: {
                200: IntegrationSyncStatusSchema,
                404: ErrorMessageSchema,
            },
        },
        getEntityChangeWarnings: {
            method: "GET",
            path: "/:financialAccountId/change-entity-warnings",
            pathParams: z.object({
                financialAccountId: z.coerce.number(),
            }),
            query: z.object({
                entityId: z.coerce.number(),
            }),
            responses: {
                200: EntityChangeWarningsSchema,
                404: ErrorMessageSchema,
            },
        },
        changeEntity: {
            method: "POST",
            path: "/:financialAccountId/change-entity",
            pathParams: z.object({
                financialAccountId: z.coerce.number(),
            }),
            body: z.object({
                entityId: z.number(),
                order: z.array(z.number()).optional(),
            }),
            responses: {
                200: c.type<FinancialAccountDto>(),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        unimportDocument: {
            method: "POST",
            path: "/:financialAccountId/financial-documents/:financialDocumentId/unimport",
            pathParams: z.object({
                financialAccountId: z.coerce.number(),
                financialDocumentId: z.coerce.number(),
            }),
            body: c.noBody(),
            responses: {
                204: c.noBody(),
                404: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/financial-account",
    },
);
